import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DAYS_OF_WEEK } from "@shared/types/foyer";
import type {
  DayOfWeek,
  OpeningHours,
  OpeningHoursDay,
  PremiseAudience,
} from "@shared/types/foyer";
import { weeklyOpenMinutes } from "@shared/opening-hours";
import { useTranslation } from "react-i18next";
import { useFormat, type Formatters } from "../i18n/format";

/**
 * When this place is open, and roughly how many people come through it.
 *
 * ## Where this lives, and where it deliberately does not
 *
 * **Location settings, not the Dashboard.** A premise owner signs in and lands
 * on the dashboard: it is a read-at-a-glance surface, and a form somebody
 * fills in once at setup both hides in it and clutters it. These belong with
 * the other things about a place that are configured and then left alone.
 *
 * The times are read in the location's own timezone, which is why the page
 * says so next to them - "09:00" is not a time until you know where.
 *
 * The two footfall figures sit here rather than anywhere else for the reason
 * they exist at all: an advertising impression is priced from visitors over
 * opening hours, so splitting them across two surfaces would let somebody fill
 * in half the sum and wonder why nothing earns.
 */

const MONDAY_UTC = Date.UTC(2024, 0, 1);
const DAY_MS = 24 * 60 * 60 * 1000;

export function dayLabel(fmt: Formatters, day: DayOfWeek): string {
  const name = fmt.date(MONDAY_UTC + DAYS_OF_WEEK.indexOf(day) * DAY_MS, {
    weekday: "long",
    timeZone: "UTC",
  });
  return name.charAt(0).toLocaleUpperCase(fmt.locale) + name.slice(1);
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** True when any open, non-24-hour day has an opens/closes that is not HH:MM. */
export function hasInvalidTimes(hours: OpeningHours | undefined): boolean {
  return DAYS_OF_WEEK.some((day) => {
    const d = hours?.[day];
    return Boolean(
      d && !d.allDay && (!HHMM.test(d.opens) || !HHMM.test(d.closes))
    );
  });
}

export interface OpeningHoursFieldsProps {
  hours: OpeningHours | undefined;
  audience: PremiseAudience | undefined;
  onChange: (next: {
    openingHours?: OpeningHours;
    audience?: PremiseAudience;
  }) => void;
}

export default function OpeningHoursFields({
  hours,
  audience,
  onChange,
}: OpeningHoursFieldsProps) {
  const { t } = useTranslation("premises");
  const fmt = useFormat();
  const patchDay = (day: DayOfWeek, next: OpeningHoursDay | undefined) => {
    const merged: OpeningHours = { ...(hours ?? {}) };
    // Deleting rather than storing a `closed: true` row: a day absent from the
    // record already MEANS closed (see `isOpenOn`), and two ways of saying the
    // same thing is how the two drift apart.
    if (!next) delete merged[day];
    else merged[day] = next;
    onChange({
      openingHours: Object.keys(merged).length > 0 ? merged : undefined,
    });
  };

  const patchAudience = (next: Partial<PremiseAudience>) => {
    const merged = { ...(audience ?? {}), ...next };
    const empty =
      merged.entriesPerWeek === undefined && merged.dwellMinutes === undefined;
    onChange({ audience: empty ? undefined : merged });
  };

  const weeklyHours = Math.round((weeklyOpenMinutes(hours) / 60) * 10) / 10;

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{ mt: 1, mb: 1, color: "text.secondary" }}
      >
        {t("openingHoursFields.heading")}
      </Typography>

      <Stack spacing={0.75}>
        {DAYS_OF_WEEK.map((day) => {
          const d = hours?.[day];
          const open = Boolean(d);
          const badOpens = Boolean(d && !d.allDay && !HHMM.test(d.opens));
          const badCloses = Boolean(d && !d.allDay && !HHMM.test(d.closes));
          const label = dayLabel(fmt, day);
          return (
            <Stack
              key={day}
              direction="row"
              spacing={1}
              alignItems="center"
              data-testid={`hours-${day}`}
            >
              <FormControlLabel
                sx={{ width: 132, mr: 0, flexShrink: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={open}
                    inputProps={{ "aria-label": label }}
                    onChange={(e) =>
                      patchDay(
                        day,
                        e.target.checked
                          ? { opens: "09:00", closes: "17:00" }
                          : undefined
                      )
                    }
                  />
                }
                label={<Typography variant="bodySm">{label}</Typography>}
              />
              {open && !d!.allDay && (
                <>
                  <TextField
                    size="small"
                    value={d!.opens}
                    error={badOpens}
                    onChange={(e) =>
                      patchDay(day, { ...d!, opens: e.target.value })
                    }
                    inputProps={{
                      "aria-label": t("openingHoursFields.opensFor", {
                        day: label,
                      }),
                      placeholder: "09:00",
                    }}
                    sx={{ width: 88 }}
                  />
                  <Typography variant="bodySm" sx={{ color: "text.secondary" }}>
                    {t("openingHoursFields.to")}
                  </Typography>
                  <TextField
                    size="small"
                    value={d!.closes}
                    error={badCloses}
                    onChange={(e) =>
                      patchDay(day, { ...d!, closes: e.target.value })
                    }
                    inputProps={{
                      "aria-label": t("openingHoursFields.closesFor", {
                        day: label,
                      }),
                      placeholder: "17:00",
                    }}
                    sx={{ width: 88 }}
                  />
                </>
              )}
              {open && (
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(d!.allDay)}
                      inputProps={{
                        "aria-label": t("openingHoursFields.allDayFor", {
                          day: label,
                        }),
                      }}
                      onChange={(e) =>
                        patchDay(day, { ...d!, allDay: e.target.checked })
                      }
                    />
                  }
                  label={
                    <Typography variant="bodySm">
                      {t("openingHoursFields.allDay")}
                    </Typography>
                  }
                />
              )}
            </Stack>
          );
        })}
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        {weeklyOpenMinutes(hours) > 0
          ? t("openingHoursFields.weeklyOpen", {
              hours: fmt.number(weeklyHours),
            })
          : t("openingHoursFields.noneOpen")}
      </Typography>

      <Typography
        variant="subtitle2"
        sx={{ mt: 2.5, mb: 1, color: "text.secondary" }}
      >
        {t("openingHoursFields.footfall")}
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          label={t("openingHoursFields.visitorsPerWeek")}
          type="number"
          size="small"
          sx={{ flex: 1 }}
          value={audience?.entriesPerWeek ?? ""}
          placeholder={t("openingHoursFields.notSet")}
          inputProps={{
            min: 0,
            "aria-label": t("openingHoursFields.visitorsPerWeek"),
          }}
          onChange={(e) =>
            patchAudience({
              entriesPerWeek: numberOrUndefined(e.target.value),
            })
          }
        />
        <TextField
          label={t("openingHoursFields.averageVisitMinutes")}
          type="number"
          size="small"
          sx={{ flex: 1 }}
          value={audience?.dwellMinutes ?? ""}
          placeholder={t("openingHoursFields.notSet")}
          inputProps={{
            min: 0,
            "aria-label": t("openingHoursFields.averageVisit"),
          }}
          onChange={(e) =>
            patchAudience({ dwellMinutes: numberOrUndefined(e.target.value) })
          }
        />
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        {t("openingHoursFields.estimateNote")}
      </Typography>
    </Box>
  );
}

/** A blank field is a RETRACTION, not a zero - "not stated" has to be sayable. */
function numberOrUndefined(raw: string): number | undefined {
  if (raw.trim() === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : undefined;
}
